// Hickey's Greenhouses — Plant Finder
// v3 — CSV-based, clean rewrite

const WORKER_URL = 'https://hickeys-plant-advisor.ashley-6fd.workers.dev';
let INVENTORY = [];
let inventoryLoaded = false;

// ── INIT ─────────────────────────────────────────────────
async function init() {
  if (!document.getElementById('customer-page')) return;
  await loadInventory();
  renderCategoryFilters();
  document.getElementById('searchBtn')?.addEventListener('click', handleSearch);
  document.getElementById('searchInput')?.addEventListener('keypress', e => { if (e.key === 'Enter') handleSearch(); });
  document.getElementById('searchInput')?.addEventListener('input', e => { if (!e.target.value.trim()) clearResults(); });
  document.getElementById('customerAskBtn')?.addEventListener('click', handleCustomerQuestion);
  document.querySelectorAll('.ai-chip, .ai-attr-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const ta = document.getElementById('customerQuestionInput');
      if (ta) { ta.value = chip.dataset.prompt; handleCustomerQuestion(); }
    });
  });
}
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();

// ── LOAD INVENTORY ───────────────────────────────────────
async function loadInventory() {
  try {
    const res = await fetch(`${WORKER_URL}/inventory-csv`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    if (!text || text.length < 10) throw new Error('Empty');
    INVENTORY = parseCSV(text);
    inventoryLoaded = INVENTORY.length > 0;
  } catch (e) {
    console.warn('Inventory load failed:', e.message);
    inventoryLoaded = false;
    INVENTORY = [];
  }
}

// ── CSV PARSER ───────────────────────────────────────────
function parseCSV(text) {
  const lines = text.split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = parseLine(lines[0]);
  const idxItem = headers.findIndex(h => /^item$/i.test(h.trim()));
  const idxCat  = headers.findIndex(h => /^category$/i.test(h.trim()));
  const idxSub  = headers.findIndex(h => /^subcategory 1$/i.test(h.trim()));
  if (idxItem < 0 || idxCat < 0) return [];

  const items = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const cols = parseLine(lines[i]);
    const name = (cols[idxItem] || '').trim();
    const cat  = (cols[idxCat]  || '').trim();
    const sub1 = idxSub >= 0 ? (cols[idxSub] || '').trim() : '';
    if (!name || !cat) continue;
    const mapped = mapCategory(name, cat, sub1);
    if (!mapped) continue;
    items.push({ name: cleanName(name, mapped), category: mapped });
  }
  return items;
}

function parseLine(line) {
  const cols = []; let cur = ''; let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { if (q && line[i+1] === '"') { cur += '"'; i++; } else q = !q; }
    else if (ch === ',' && !q) { cols.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  cols.push(cur.trim());
  return cols;
}

// ── FILTERS & CATEGORY MAP ───────────────────────────────
function mapCategory(name, cat, sub1) {
  const n   = name.toLowerCase();
  const c   = cat.toLowerCase();
  const s   = sub1.toLowerCase();

  // ── Hard exclude: sale in any form ──
  if (/\bsale\b|\bregatta\b|\bcanada day\b/i.test(name)) return null;

  // ── Exclude categories ──
  const badCats = ['christmas','fall','home decor','delivery','gift cards','foods','patio','pots & planters'];
  if (badCats.some(x => c.includes(x))) {
    // Keep fall mums under Annuals
    if (n.includes('mum') && (c.includes('annuals') || c.includes('fall') || s.includes('annuals'))) return 'Annuals';
    return null;
  }

  // ── Exclude sub-categories ──
  const badSubs = ['pots & planters','stoneware','home décor','home decor','coco liner','landscape fabric','mulch','patio furniture','christmas','fall décor','fall wreaths','garden decor','wreaths','artificial'];
  if (badSubs.some(x => s.includes(x))) return null;

  // ── Exclude name keywords ──
  const badWords = ['clearance','tinsel','flocked','frosted','burlap','santa','ornament','ribbon','wreath','artificial','mug','frame','figurine','fountain','lantern','chime','gnome','spinner','feeder','birdbath','dispenser','keychain','magnet','shot glass','lighthouse','bracket','sprinkler','vermiculite','perlite','garden guide','skirt','collar'];
  if (badWords.some(k => n.includes(k))) return null;

  // ── Exclude name patterns ──
  const badPatterns = [
    /^tree type \d+/i, /^shrub type \d+/i, /^perennial type \d+/i,
    /^fruit tree type \d+/i,
    /^annuals?\s*-?\s*\d+/i,
    /^vegetable\s*-?\s*\d+/i, /^vegetable$/i,
    /^herbs?\s*\d+/i,
    /clearance/i,
    /type \d+$/i,
    /^copy of /i,
    /nova scotia christmas/i,
    /champagne boxwood spray/i,
    /basket.*\$\d+/i,
    /^flower arrangements?$/i,
    /^fresh flower bouquet$/i,
    /^\d+ IN /i,
    /^bush$/i,
    /^thyme$/i,
    /^thymus/i,
    /^hydrangea bouquet/i,
    /^hydrangea bundle/i,
    /^single wired hydrangea stem/i,
    /^single hydrangea stem/i,
    /hanging bag/i,
    /hanging basket/i,
  ];
  if (badPatterns.some(p => p.test(name))) return null;

  // ── Hydrangeas always → Perennials ──
  if (/hydrangea|bloomstruck/i.test(name)) return 'Perennials';

  // ── Wildflowers → Bulbs & Seeds ──
  if (/wildflower/i.test(name)) return 'Bulbs & Seeds';

  // ── Map to display category ──
  if (c === 'plants') {
    if (s.includes('hanging basket')) return 'Hanging Baskets';
    if (s === 'annuals')              return 'Annuals';
    if (s === 'perennials')           return 'Perennials';
    if (s === 'vegetables')           return 'Vegetables';
    if (s === 'herbs')                return 'Herbs';
    if (s === 'house plants')         return 'House Plants';
    if (s === 'bulbs' || s === 'seeds') return 'Bulbs & Seeds';
    if (s === 'planters')             return 'Custom Planters';
  }
  if (c === 'perennials') return 'Perennials';
  if (c.includes('trees') && c.includes('shrub')) return 'Trees and Shrubs';
  if (c.includes('gardening supplie')) {
    if (s === 'fertilizer')      return 'Fertilizer';
    if (s === 'soil')            return 'Soil';
    if (s.includes('pest'))      return 'Pest, Weed and Disease Control';
    if (s.includes('tool') || s === '') return 'Gardening Supplies';
    return 'Gardening Supplies';
  }
  if (c === 'fresh flowers') return null; // shown as static card only
  if (c === 'artificial flowers') return null;
  return null;
}

function cleanName(name, category) {
  let n = name;
  // Remove pack codes like (806)
  n = n.replace(/\s*\(\d+\)\s*/g, ' ').trim();
  // Remove pot size at end
  n = n.replace(/\s+\d+(\.\d+)?"?\s*$/, '').trim();
  // BloomStruck → Hydrangea BloomStruck
  if (/^bloomstruck$/i.test(n)) n = 'Hydrangea BloomStruck';
  else if (/bloomstruck/i.test(n) && !/hydrangea/i.test(n)) n = n.replace(/bloomstruck/i, 'Hydrangea BloomStruck');
  return n;
}

// ── CATEGORY FILTERS ─────────────────────────────────────
// Ordered by real customer click data — most-clicked first.
// First 6 stay visible on mobile; the rest collapse behind "Show all".
const ALL_CATS = [
  'Perennials','Annuals','Hanging Baskets','Trees and Shrubs',
  'Vegetables','House Plants','Bulbs & Seeds','Herbs',
  'Fertilizer','Soil','Custom Planters','Floral Arrangements',
  'Gardening Supplies','Pest, Weed and Disease Control'
];
const MOBILE_VISIBLE_COUNT = 6;
const STATIC_ONLY_CATS = new Set(['Herbs','Floral Arrangements','Custom Planters','House Plants']);

function renderCategoryFilters() {
  const container = document.getElementById('categoryFilters');
  if (!container) return;
  ALL_CATS.forEach((cat, i) => {
    const label = document.createElement('label');
    if (i >= MOBILE_VISIBLE_COUNT) label.classList.add('chip-secondary');
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.value = cat;
    cb.addEventListener('change', () => {
      label.classList.toggle('active', cb.checked);
      if (cb.checked) logCategoryToWorker(cat);
      handleSearch();
    });
    label.appendChild(cb);
    label.appendChild(document.createTextNode(cat));
    container.appendChild(label);
  });

  // Mobile-only "Show all categories" toggle (hidden on desktop via CSS)
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'cat-toggle-btn';
  toggle.textContent = `Show all categories (${ALL_CATS.length})`;
  toggle.setAttribute('aria-expanded', 'false');
  toggle.addEventListener('click', () => {
    const expanded = container.classList.toggle('expanded');
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? 'Show fewer categories' : `Show all categories (${ALL_CATS.length})`;
  });
  container.appendChild(toggle);
}

// ── SEARCH ───────────────────────────────────────────────
function handleSearch() {
  const query    = (document.getElementById('searchInput')?.value || '').toLowerCase().trim();
  const selected = [...document.querySelectorAll('.category-filters input:checked')].map(cb => cb.value);
  if (!query && selected.length === 0) { clearResults(); return; }

  const results = INVENTORY.filter(item => {
    const matchQ   = !query || item.name.toLowerCase().includes(query) || item.category.toLowerCase().includes(query);
    const matchCat = selected.length === 0 || selected.includes(item.category);
    return matchQ && matchCat;
  });

  displayResults(results, query, selected);
}

function clearResults() {
  const s = document.getElementById('resultsSection');
  if (s) s.innerHTML = '';
}

// ── DISPLAY RESULTS ──────────────────────────────────────
function displayResults(results, query, selected) {
  const section = document.getElementById('resultsSection');
  section.innerHTML = '';

  const cats  = new Set(results.map(r => r.category));
  const selSet = new Set(selected);

  // ── Static / info-only categories ──

  // Hanging Baskets — static card first
  if (selSet.has('Hanging Baskets') || cats.has('Hanging Baskets') || /hanging|basket/i.test(query)) {
    section.appendChild(buildStaticCard(
      'Hanging Baskets', 'Hanging Baskets',
      'We offer a range of custom hanging baskets in store — drop by to get yours today!',
      null, false, true // directions CTA
    ));
  }

  // Floral Arrangements
  if (selSet.has('Floral Arrangements')) {
    section.appendChild(buildStaticCard('Fresh Floral Arrangements','Floral Arrangements',
      'We do custom fresh floral arrangements for every occasion. Give us a call and we\'ll put something together for you.',null,true));
    section.appendChild(buildStaticCard('Everlasting (Artificial) Arrangements','Floral Arrangements',
      'Looking for something that lasts? We carry a beautiful selection of everlasting artificial arrangements — perfect for year-round decorating, memorials, or a lasting gift. Drop by to see what\'s available.',null,true));
  }

  // Custom Planters
  if (selSet.has('Custom Planters') || cats.has('Custom Planters')) {
    section.appendChild(buildStaticCard('Custom Planters','Custom Planters',
      'We design and build custom planters in-house. Whether you have something specific in mind or want our staff to create something beautiful for your space, we\'d love to help. To place a custom order, call or visit us in Kelligrews.',null,true));
  }

  // House Plants
  if (selSet.has('House Plants') || cats.has('House Plants')) {
    section.appendChild(buildInfoNote('House Plants',
      'We carry a wide variety of house plants in store — tropicals, succulents, flowering varieties, and more. Selection changes throughout the season, so visit us to see what\'s in today.',
      null, false
    ));
  }

  // Herbs
  if (selSet.has('Herbs')) {
    section.appendChild(buildInfoNote('Herbs',
      'We carry a wide variety of herbs each season including basil, thyme, mint, chives, parsley, rosemary, oregano, cilantro, dill, and more. Selection changes throughout the season — call or drop in to check what\'s available.',
      '709-834-5525'
    ));
  }

  // Vegetables note
  if (selSet.has('Vegetables') || cats.has('Vegetables') || /vegetable|tomato|pepper|lettuce|potato/i.test(query)) {
    section.appendChild(buildInfoNote('Vegetables',
      'We carry a wide variety each season — tomatoes, peppers, lettuce, cucumber, zucchini, kale, seed potatoes, strawberries, and more. What\'s listed here is just a sample, call or drop by to see what\'s in.',
      '709-834-5525'
    ));
  }

  // Gardening Supplies note
  if (selSet.has('Gardening Supplies') || cats.has('Gardening Supplies')) {
    section.appendChild(buildInfoNote('Gardening Supplies',
      'We carry a full range of tools, hardware, pots and planters, stoneware, mulch, and garden décor in store. What\'s listed here is just a sample — call or drop by to see the full selection.',
      '709-834-5525'
    ));
  }

  // Pest note
  if (selSet.has('Pest, Weed and Disease Control') || cats.has('Pest, Weed and Disease Control')) {
    section.appendChild(buildInfoNote('Pest, Weed & Disease Control',
      'We carry organic sprays, insecticidal soaps, slug bait, weed control, and fungicides. Not sure what you\'re dealing with? Bring a photo and our staff will help point you in the right direction.',
      '709-834-5525'
    ));
  }

  // Filter out categories shown as static/info-only
  const cardItems = results.filter(r =>
    r.category !== 'Floral Arrangements' &&
    r.category !== 'Custom Planters' &&
    r.category !== 'House Plants' &&
    r.category !== 'Herbs'
  );

  // Empty state
  if (cardItems.length === 0 && section.children.length === 0) {
    if (!inventoryLoaded) {
      section.appendChild(buildInfoNote('','Having trouble loading our inventory right now. Please call us or try refreshing the page.','709-834-5525'));
    } else if (query) {
      const el = document.createElement('div');
      el.className = 'results-empty';
      const p = document.createElement('p');
      p.appendChild(document.createTextNode('No results for "'));
      const strong = document.createElement('strong');
      strong.textContent = query; // safe text insertion — never innerHTML with user input
      p.appendChild(strong);
      p.appendChild(document.createTextNode('". Try a different search, or ask the gardening guide below.'));
      el.appendChild(p);
      section.appendChild(el);
    }
    return;
  }

  // Group and render cards
  const groups = groupByBase(cardItems);
  for (const group of groups) {
    section.appendChild(buildPlantCard(group));
  }

  // Copy results button
  if (groups.length > 0) {
    const copyWrap = document.createElement('div');
    copyWrap.className = 'copy-results-wrap';
    const copyBtn = document.createElement('button');
    copyBtn.className = 'btn-ghost copy-results-btn';
    copyBtn.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy my results`;
    copyBtn.addEventListener('click', () => copySearchResults(groups, query, selected));
    copyWrap.appendChild(copyBtn);
    section.appendChild(copyWrap);
  }
}

// ── GROUP BY BASE NAME ────────────────────────────────────
function groupByBase(items) {
  const map = {};
  for (const item of items) {
    const base = item.name
      .replace(/\s*[-–]\s*(red|orange|yellow|white|pink|purple|blue|green|mix|rose|coral|peach|burgundy|lavender|cream|gold|mauve|salmon|multi|bicolor|violet|fuchsia|magenta|scarlet|lilac|teal|maroon|navy|lime|amber|ivory|black|silver|bronze)\s*$/i, '')
      .trim();
    const key = `${base}__${item.category}`;
    if (!map[key]) map[key] = { baseName: base, category: item.category, variants: [] };
    const m = item.name.match(/[-–]\s*([A-Za-z][A-Za-z\s\/]+)$/);
    if (m) { const v = m[1].trim(); if (v && !map[key].variants.includes(v)) map[key].variants.push(v); }
  }
  return Object.values(map).sort((a, b) => a.baseName.localeCompare(b.baseName));
}


// ── PLANT ATTRIBUTES (genus-level, curated) ──────────────
// At-a-glance sun/water guidance + pet safety for common genera.
// Pet safety follows ASPCA toxic/non-toxic plant lists:
//   pets: 'safe'  → "Pet safe" badge
//   pets: 'toxic' → "Toxic to pets" badge
//   pets: null    → no badge shown (unknown/ambiguous — never guess)
// Matching is word-boundary based against the plant's base name.
// MORE SPECIFIC KEYS MUST COME BEFORE GENERAL ONES
// (e.g. 'lily of the valley' and 'daylily' before 'lily',
//  'asparagus fern' before 'fern', 'rose of sharon' before 'rose').
const PLANT_ATTRS = [
  // ── Multi-word / specific first ──
  ['lily of the valley', { sun: 'Part–full shade',   water: 'Moderate water',  pets: 'toxic' }],
  ['rose of sharon',     { sun: 'Full sun',          water: 'Moderate water',  pets: 'toxic' }],
  ['asparagus fern',     { sun: 'Part shade',        water: 'Keep moist',      pets: 'toxic' }],
  ['bleeding heart',     { sun: 'Part–full shade',   water: 'Keep moist',      pets: 'toxic' }],
  ['black-eyed susan',   { sun: 'Full sun',          water: 'Moderate water',  pets: 'safe'  }],
  ['coral bells',        { sun: 'Part shade',        water: 'Moderate water',  pets: 'safe'  }],
  ['bee balm',           { sun: 'Sun–part shade',    water: 'Keep moist',      pets: 'safe'  }],
  ['sweet pea',          { sun: 'Full sun',          water: 'Moderate water',  pets: 'toxic' }],
  ['million bells',      { sun: 'Full sun',          water: 'Water regularly', pets: 'safe'  }],
  ['daylily',            { sun: 'Sun–part shade',    water: 'Moderate water',  pets: 'toxic' }],
  ['hemerocallis',       { sun: 'Sun–part shade',    water: 'Moderate water',  pets: 'toxic' }],

  // ── Perennials ──
  ['hosta',        { sun: 'Part–full shade', water: 'Moderate water',   pets: 'toxic' }],
  ['hydrangea',    { sun: 'Sun–part shade',  water: 'Keep moist',       pets: 'toxic' }],
  ['astilbe',      { sun: 'Part–full shade', water: 'Keep moist',       pets: 'safe'  }],
  ['coneflower',   { sun: 'Full sun',        water: 'Drought tolerant', pets: 'safe'  }],
  ['echinacea',    { sun: 'Full sun',        water: 'Drought tolerant', pets: 'safe'  }],
  ['rudbeckia',    { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['heuchera',     { sun: 'Part shade',      water: 'Moderate water',   pets: 'safe'  }],
  ['peony',        { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['lupin',        { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['lupine',       { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['delphinium',   { sun: 'Full sun',        water: 'Keep moist',       pets: 'toxic' }],
  ['foxglove',     { sun: 'Part shade',      water: 'Moderate water',   pets: 'toxic' }],
  ['phlox',        { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['sedum',        { sun: 'Full sun',        water: 'Drought tolerant', pets: 'safe'  }],
  ['iris',         { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['primrose',     { sun: 'Part shade',      water: 'Keep moist',       pets: 'toxic' }],
  ['primula',      { sun: 'Part shade',      water: 'Keep moist',       pets: 'toxic' }],
  ['clematis',     { sun: 'Full sun',        water: 'Keep moist',       pets: 'toxic' }],
  ['monkshood',    { sun: 'Sun–part shade',  water: 'Keep moist',       pets: 'toxic' }],
  ['hollyhock',    { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['columbine',    { sun: 'Sun–part shade',  water: 'Moderate water',   pets: null    }],
  ['fern',         { sun: 'Shade',           water: 'Keep moist',       pets: null    }],

  // ── Annuals & basket stuffers ──
  ['petunia',      { sun: 'Full sun',        water: 'Water regularly',  pets: 'safe'  }],
  ['calibrachoa',  { sun: 'Full sun',        water: 'Water regularly',  pets: 'safe'  }],
  ['impatiens',    { sun: 'Shade–part shade',water: 'Keep moist',       pets: 'safe'  }],
  ['begonia',      { sun: 'Part shade',      water: 'Moderate water',   pets: 'toxic' }],
  ['geranium',     { sun: 'Full sun',        water: 'Let dry between',  pets: 'toxic' }],
  ['pelargonium',  { sun: 'Full sun',        water: 'Let dry between',  pets: 'toxic' }],
  ['zinnia',       { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['snapdragon',   { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'safe'  }],
  ['sunflower',    { sun: 'Full sun',        water: 'Water regularly',  pets: 'safe'  }],
  ['marigold',     { sun: 'Full sun',        water: 'Moderate water',   pets: null    }],
  ['pansy',        { sun: 'Sun–part shade',  water: 'Keep moist',       pets: null    }],
  ['viola',        { sun: 'Sun–part shade',  water: 'Keep moist',       pets: null    }],
  ['lobelia',      { sun: 'Sun–part shade',  water: 'Keep moist',       pets: 'toxic' }],
  ['alyssum',      { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['fuchsia',      { sun: 'Shade–part shade',water: 'Keep moist',       pets: 'safe'  }],
  ['nasturtium',   { sun: 'Full sun',        water: 'Low water',        pets: 'safe'  }],
  ['salvia',       { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['celosia',      { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['aster',        { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['dahlia',       { sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['chrysanthemum',{ sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['mum',          { sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['dianthus',     { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['carnation',    { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['gladiolus',    { sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['lavender',     { sun: 'Full sun',        water: 'Low water',        pets: 'toxic' }],

  // ── Bulbs ──
  ['tulip',        { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['daffodil',     { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['narcissus',    { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['allium',       { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['lily',         { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],

  // ── Trees & shrubs ──
  ['lilac',        { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['forsythia',    { sun: 'Full sun',        water: 'Moderate water',   pets: 'safe'  }],
  ['azalea',       { sun: 'Part shade',      water: 'Keep moist',       pets: 'toxic' }],
  ['rhododendron', { sun: 'Part shade',      water: 'Keep moist',       pets: 'toxic' }],
  ['boxwood',      { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['yew',          { sun: 'Sun–shade',       water: 'Moderate water',   pets: 'toxic' }],
  ['holly',        { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['euonymus',     { sun: 'Sun–part shade',  water: 'Moderate water',   pets: 'toxic' }],
  ['wisteria',     { sun: 'Full sun',        water: 'Moderate water',   pets: 'toxic' }],
  ['rose',         { sun: 'Full sun',        water: 'Deep weekly water',pets: 'safe'  }],

  // ── Vegetables & fruit ──
  ['tomato',       { sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['potato',       { sun: 'Full sun',        water: 'Water regularly',  pets: 'toxic' }],
  ['pepper',       { sun: 'Full sun',        water: 'Water regularly',  pets: null    }],
  ['cucumber',     { sun: 'Full sun',        water: 'Keep moist',       pets: 'safe'  }],
  ['zucchini',     { sun: 'Full sun',        water: 'Water regularly',  pets: 'safe'  }],
  ['kale',         { sun: 'Sun–part shade',  water: 'Water regularly',  pets: null    }],
  ['lettuce',      { sun: 'Sun–part shade',  water: 'Keep moist',       pets: null    }],
  ['strawberry',   { sun: 'Full sun',        water: 'Water regularly',  pets: 'safe'  }],
];

function lookupPlantAttrs(name) {
  const n = String(name || '').toLowerCase();
  for (const [key, attrs] of PLANT_ATTRS) {
    // \b word boundaries + optional plural 's' (e.g. matches "Hostas")
    if (new RegExp(`\\b${key}s?\\b`).test(n)) return attrs;
  }
  return null;
}

// Static icon markup (never contains user input)
const SUN_ICON_SVG = '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1l2.1-2.1M17 7l2.1-2.1"/></svg>';
const DROP_ICON_SVG = '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12z"/></svg>';

function buildCareItem(iconSvg, label) {
  const span = document.createElement('span');
  span.innerHTML = iconSvg;
  span.appendChild(document.createTextNode(' ' + label));
  return span;
}

// ── CARD BUILDERS ────────────────────────────────────────
function buildPlantCard(group) {
  const card = document.createElement('div');
  card.className = 'plant-card';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-expanded', 'false');
  card.setAttribute('aria-label', `${group.baseName} — press Enter for care tips`);

  const catEl = document.createElement('span');
  catEl.className = 'plant-category';
  catEl.textContent = group.category;
  card.appendChild(catEl);

  const h3 = document.createElement('h3');
  h3.textContent = group.baseName;

  const attrs = lookupPlantAttrs(group.baseName);
  const head = document.createElement('div');
  head.className = 'plant-card-head';
  head.appendChild(h3);
  if (attrs && (attrs.pets === 'safe' || attrs.pets === 'toxic')) {
    const pet = document.createElement('span');
    pet.className = attrs.pets === 'safe' ? 'pet-badge safe' : 'pet-badge caution';
    pet.textContent = attrs.pets === 'safe' ? 'Pet safe' : 'Toxic to pets';
    head.appendChild(pet);
  }
  card.appendChild(head);

  const badge = document.createElement('div');
  badge.className = 'carry-badge';
  badge.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> We usually carry this — call or visit to confirm.`;
  card.appendChild(badge);

  if (group.variants.length > 1) {
    const v = document.createElement('p');
    v.className = 'plant-variants';
    v.textContent = `Available in: ${group.variants.join(', ')}`;
    card.appendChild(v);
  }

  if (attrs && (attrs.sun || attrs.water)) {
    const care = document.createElement('div');
    care.className = 'plant-care-icons';
    if (attrs.sun)   care.appendChild(buildCareItem(SUN_ICON_SVG, attrs.sun));
    if (attrs.water) care.appendChild(buildCareItem(DROP_ICON_SVG, attrs.water));
    card.appendChild(care);
  }

  card.addEventListener('click', () => handleCardExpand(card, group.baseName));
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardExpand(card, group.baseName);
    }
  });
  return card;
}

function buildStaticCard(title, category, description, tip, callCTA, directionsCTA) {
  const card = document.createElement('div');
  card.className = 'plant-card static-card';

  const catEl = document.createElement('span');
  catEl.className = 'plant-category';
  catEl.textContent = category;
  card.appendChild(catEl);

  const h3 = document.createElement('h3');
  h3.textContent = title;
  card.appendChild(h3);

  const desc = document.createElement('p');
  desc.className = 'plant-description';
  desc.textContent = description;
  card.appendChild(desc);

  if (callCTA) {
    const a = document.createElement('a');
    a.href = 'tel:17098345525';
    a.className = 'btn-primary plant-cta';
    a.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.37 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 5.59 5.59l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg> Call to Order`;
    card.appendChild(a);
  }
  if (directionsCTA) {
    const a = document.createElement('a');
    a.href = 'https://www.google.com/maps/dir/?api=1&destination=689+CBS+Highway,+Kelligrews,+NL';
    a.target = '_blank';
    a.className = 'btn-primary plant-cta';
    a.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg> Get Directions`;
    card.appendChild(a);
  }
  return card;
}

function buildInfoNote(title, text, phone, directions) {
  const el = document.createElement('div');
  el.className = 'results-category-note';
  let html = title ? `<strong>${title}</strong> ` : '';
  html += text;
  if (phone) html += ` <a href="tel:1${phone.replace(/\D/g,'')}">Call us</a>`;
  if (directions) html += ` or <a href="https://www.google.com/maps/dir/?api=1&destination=689+CBS+Highway,+Kelligrews,+NL" target="_blank">get directions</a>.`;
  el.innerHTML = `<p>${html}</p>`;
  return el;
}

// ── CARD EXPAND (AI care guide) ───────────────────────────
async function handleCardExpand(card, plantName) {
  const existing = card.querySelector('.plant-care-detail');
  if (existing) { existing.remove(); card.setAttribute('aria-expanded', 'false'); card.classList.remove('expanded'); return; }
  card.setAttribute('aria-expanded', 'true');
  card.classList.add('expanded');
  const detail = document.createElement('div');
  detail.className = 'plant-care-detail';
  detail.setAttribute('role', 'status');
  detail.innerHTML = '<div class="loading-dots" aria-hidden="true"><span></span><span></span><span></span></div><span class="visually-hidden">Loading care tips</span>';
  card.appendChild(detail);
  try {
    const res = await fetch(`${WORKER_URL}/advice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: `What are the basic care requirements for ${plantName} in a Newfoundland garden? Cover light needs, watering, and any NL-specific tips. 3 to 4 sentences.`, inventory: [] })
    });
    const data = await res.json();
    detail.innerHTML = `<p>${data.response || 'Ask our gardening guide below for care tips.'}</p>`;
  } catch {
    detail.innerHTML = '<p>Ask our gardening guide below for care tips on this plant.</p>';
  }
}

// ── AI GARDENING GUIDE ────────────────────────────────────
async function handleCustomerQuestion() {
  const input  = document.getElementById('customerQuestionInput');
  const output = document.getElementById('customerResponseOutput');
  const btn    = document.getElementById('customerAskBtn');
  if (!input || !output || !btn) return;
  const question = input.value.trim();
  if (!question) return;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.textContent = 'Looking that up...';
  output.style.display = 'block';
  output.innerHTML = `<div class="ai-loading"><div class="loading-dots" aria-hidden="true"><span></span><span></span><span></span></div><p>Looking that up for you...</p></div>`;
  try {
    const res = await fetch(`${WORKER_URL}/advice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, inventory: [] })
    });
    const data = await res.json();
    const responseText = data.response || 'Something went wrong. Call us at 709-834-5525.';
    output.innerHTML = `<p>${responseText.replace(/\n/g,'<br>')}</p>
      <button class="btn-ghost copy-advice-btn" id="copyAdviceBtn">
        <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy this advice
      </button>`;
    // Log question to staff dashboard
    logQuestionToWorker(question, responseText);
    // Attach copy handler via JS (avoids inline onclick quote-escaping issues)
    const copyBtn = document.getElementById('copyAdviceBtn');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => copyAdvice(copyBtn, question, responseText));
    }
  } catch {
    output.innerHTML = '<p>Something went wrong. Call us at 709-834-5525.</p>';
  } finally {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.textContent = 'Get Advice';
  }
}

// ── LOG CATEGORY TO WORKER ──────────────────────────────
async function logCategoryToWorker(category) {
  try {
    await fetch(`${WORKER_URL}/log-category`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category })
    });
  } catch (e) { /* silent fail */ }
}

// ── LOG QUESTION TO WORKER ───────────────────────────────
async function logQuestionToWorker(question, response) {
  try {
    await fetch(`${WORKER_URL}/log-question`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, response })
    });
  } catch (e) {
    // Silent fail — don't interrupt user experience
  }
}

// ── COPY HELPERS ─────────────────────────────────────────
function copySearchResults(groups, query, selected) {
  const lines = [];
  lines.push("Hickey's Plant Finder — Results");
  lines.push('hickeysgreenhouses.com/pages/plant-finder-gardening-guide');
  lines.push('');
  if (query) lines.push(`Search: "${query}"`);
  if (selected && selected.length) lines.push(`Category: ${selected.join(', ')}`);
  lines.push('');
  groups.forEach(g => {
    let line = `• ${g.baseName} (${g.category})`;
    if (g.variants.length > 1) line += ` — ${g.variants.join(', ')}`;
    lines.push(line);
  });
  lines.push('');
  lines.push('We usually carry these — call or visit to confirm.');
  lines.push('📞 709-834-5525 | 689 CBS Highway, Kelligrews, NL');

  const text = lines.join('\n');
  navigator.clipboard.writeText(text).then(() => {
    const btn = document.querySelector('.copy-results-btn');
    if (btn) { btn.textContent = '✓ Copied!'; setTimeout(() => { btn.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy my results`; }, 2500); }
  }).catch(() => {
    const btn = document.querySelector('.copy-results-btn');
    if (btn) btn.textContent = 'Copy not supported in this browser';
  });
}

function copyAdvice(btn, question, response) {
  const lines = [];
  lines.push("Hickey's Greenhouses — Gardening Advice");
  lines.push('hickeysgreenhouses.com/pages/plant-finder-gardening-guide');
  lines.push('');
  lines.push(`Q: ${question}`);
  lines.push('');
  lines.push(response);
  lines.push('');
  lines.push('Always confirm with our staff if you\'re unsure.');
  lines.push('📞 709-834-5525 | 689 CBS Highway, Kelligrews, NL');

  const text = lines.join('\n');
  navigator.clipboard.writeText(text).then(() => {
    btn.textContent = '✓ Copied!';
    setTimeout(() => { btn.innerHTML = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy this advice`; }, 2500);
  }).catch(() => {
    btn.textContent = 'Copy not supported in this browser';
  });
}